import OccasionPicker from '@/components/form/OccasionPicker'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { useStore } from '@tanstack/react-form'

export const createSeasonalScheduleValues = () => ({
  seasonal_tag_id: null,
  timing_mode: 'occasion_offset',
  days_before: 14,
  days_after: 1,
  start_month: 1,
  start_day: 1,
  end_month: 12,
  end_day: 31,
  end_is_last_day: false,
  provision_new_organizations: false,
  default_enabled: false,
})

const fieldName = (path, name) => (path ? `${path}.${name}` : name)

const valueAtPath = (values, path) => {
  if (!path) return values

  return path.split('.').reduce((value, segment) => value?.[segment], values)
}

export function SeasonalScheduleTagField({ form, path = '', options }) {
  return (
    <form.AppField name={fieldName(path, 'seasonal_tag_id')}>
      {() => (
        <OccasionPicker
          label="Occasion or season"
          dialogDescription="Choose one occasion or season for this schedule."
          mode="single"
          options={options}
          placeholder="Choose an occasion or season"
          requireSelection
          onValueChange={(seasonalTagId) => {
            const selected = options.find((option) => Number(option.id) === Number(seasonalTagId))
            form.setFieldValue(
              fieldName(path, 'timing_mode'),
              selected?.tag_type === 'season' ? 'annual_range' : 'occasion_offset'
            )
          }}
        />
      )}
    </form.AppField>
  )
}

export function SeasonalScheduleTimingFields({ form, path = '', isReadOnly, layout = 'row' }) {
  const timingMode = useStore(form.store, (state) => valueAtPath(state.values, path)?.timing_mode)
  const endIsLastDay = useStore(form.store, (state) =>
    Boolean(valueAtPath(state.values, path)?.end_is_last_day)
  )
  const isSheet = layout === 'sheet'

  if (timingMode === 'annual_range') {
    return (
      <div className={isSheet ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-2 gap-3 sm:grid-cols-4'}>
        <form.AppField name={fieldName(path, 'start_month')}>
          {(field) => (
            <field.TextInput
              type="number"
              min="1"
              max="12"
              label="Start month"
              disabled={isReadOnly}
            />
          )}
        </form.AppField>
        <form.AppField name={fieldName(path, 'start_day')}>
          {(field) => (
            <field.TextInput
              type="number"
              min="1"
              max="31"
              label="Start day"
              disabled={isReadOnly}
            />
          )}
        </form.AppField>
        <form.AppField name={fieldName(path, 'end_month')}>
          {(field) => (
            <field.TextInput
              type="number"
              min="1"
              max="12"
              label="End month"
              disabled={isReadOnly}
            />
          )}
        </form.AppField>
        {!endIsLastDay ? (
          <form.AppField name={fieldName(path, 'end_day')}>
            {(field) => (
              <field.TextInput
                type="number"
                min="1"
                max="31"
                label="End day"
                disabled={isReadOnly}
              />
            )}
          </form.AppField>
        ) : (
          <div className="flex w-full flex-col gap-y-2">
            <Label>End day</Label>
            <Typography variant="supporting" className="flex h-10 items-center">
              Last day
            </Typography>
          </div>
        )}
        <div className={isSheet ? 'col-span-2' : 'col-span-2 sm:col-span-4'}>
          <form.AppField name={fieldName(path, 'end_is_last_day')}>
            {(field) => (
              <field.SwitchInput
                label="Use last day of end month"
                description="Handles leap years and month length automatically."
                disabled={isReadOnly}
              />
            )}
          </form.AppField>
        </div>
      </div>
    )
  }

  return (
    <div className={isSheet ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-2 gap-3'}>
      <form.AppField name={fieldName(path, 'days_before')}>
        {(field) => (
          <field.TextInput
            type="number"
            min="0"
            max="90"
            label="Days before"
            disabled={isReadOnly}
          />
        )}
      </form.AppField>
      <form.AppField name={fieldName(path, 'days_after')}>
        {(field) => (
          <field.TextInput
            type="number"
            min="0"
            max="90"
            label="Days after"
            disabled={isReadOnly}
          />
        )}
      </form.AppField>
    </div>
  )
}

export function SeasonalScheduleDefaultsFields({ form, path = '', isReadOnly, layout = 'row' }) {
  return (
    <div className={layout === 'sheet' ? 'grid gap-4' : 'grid gap-3 sm:grid-cols-2 xl:grid-cols-1'}>
      <form.AppField name={fieldName(path, 'provision_new_organizations')}>
        {(field) => (
          <field.SwitchInput
            id={`${path || 'new-schedule'}-provision-new-organizations`}
            label="Create by default"
            description="Create this update for future florists."
            disabled={isReadOnly}
          />
        )}
      </form.AppField>
      <form.AppField name={fieldName(path, 'default_enabled')}>
        {(field) => (
          <field.SwitchInput
            id={`${path || 'new-schedule'}-default-enabled`}
            label="Enable by default"
            description="Turn the new florist's schedule on when created."
            disabled={isReadOnly}
          />
        )}
      </form.AppField>
    </div>
  )
}
